import { gql } from 'graphql-request';
import firebase from 'firebase/app';

import {
  baseApi,
  select,
  insert,
  update,
  afterInsert,
  afterUpdate,
  beforeUpdate,
  objectToStr,
  ws,
  escapeBackslashes,
  buildPaginationClauses,
} from './base';

import {
  productType,
  productSearchType,
  messageType,
  productCompanyType,
  productRecommendationType,
} from '../graphqlTypes';

const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: select(builder, 'product', productType, false, {
      filter: (params) =>
        `{${params.item ? `item: {_eq: "${params.item}"},` : ''} 
          ${params.section ? `section: {_eq: "${params.section}"},` : ''} 
          ${params.uniclass ? `uniclass: {_eq: "${params.uniclass}"},` : ''} 
          ${params.omniclass ? `omniclass: {_eq: "${params.omniclass}"},` : ''} 
          ${params.caws ? `caws: {_eq: "${params.caws}"},` : ''} 
          ${params.manufacturer_id ? `manufacturer_id: {_eq: ${params.manufacturer_id}},` : ''} 
          ${params.consultancy_id ? `consultancy_id: {_eq: ${params.consultancy_id}},` : ''}
          ${
            params.regions
              ? `regions: {_has_keys_any: [${params.regions.map((r) => `"${r}"`).join(', ')}]},`
              : ''
          }
          ${
            params.status
              ? `status: {_in: [${params.status.map((s) => `"${s}"`).join(', ')}]},`
              : ''
          }
         }`,
      order: `[
        { product_order: {plan: asc} },
        { primary_product_company: {is_star: desc_nulls_last} },
        { product_order: {certifictation_num: desc} },
        { company: { name: asc } },
        { name: asc }
      ]`,
    }),
    searchProducts: select(builder, 'product', productSearchType, false, {
      filter: (params) => {
        return `{${
          params.name ? `name: {_ilike: "%${params.name}%"}, status: {_neq: "archived"}` : ''
        }
          ${
            params.manufacturers
              ? `manufacturer_id: {_in: [${params.manufacturers.join(',')}]},`
              : ''
          } 
         }`;
      },
      order: `[
        { company: { name: asc } },
        { name: asc }
      ]`,
      limit: 50,
    }),
    getProductsFilter: select(builder, 'product', productType, false, {
      query: (params = {}) => {
        const payload = params.payload || {};

        // Build where _and conditions
        const andConditions = [];

        if (payload.item) {
          andConditions.push(`{ item: { _eq: "${payload.item}" } }`);
        }
        if (payload.name) {
          andConditions.push(`{ name: { _ilike: "%${payload.name}%" } }`);
        }
        if (payload.section) {
          andConditions.push(`{ section: { _eq: "${payload.section}" } }`);
        }
        if (payload.manufacturer) {
          andConditions.push(`{ manufacturer_id: { _eq: ${payload.manufacturer} } }`);
        }
        if (payload.consultancy_id) {
          andConditions.push(`{ consultancy_id: { _eq: ${payload.consultancy_id} } }`);
        }

        // Architect products filter
        if (payload.forCompany) {
          andConditions.push(`{ for_company_id: { _eq: ${payload.forCompany} } }`);
        } else {
          andConditions.push(
            `{ for_company_id: { _is_null: ${!payload.onlyArchitectProducts} } }`
          );
        }

        if (payload.profile) {
          andConditions.push(`{ updated_by: { _eq: "${payload.profile}" } }`);
        }

        if (payload.regions && payload.regions.length > 0) {
          const regionConditions = payload.regions
            .map((r) => `{ regions: { _contains: ${JSON.stringify([r])} } }`)
            .join(', ');
          andConditions.push(`{ _or: [ ${regionConditions} ] }`);
        }

        // Status / archived handling
        // archived=true → no status filter (shows archived IN ADDITION to non-archived)
        // archived=false → exclude archived
        if (payload.status) {
          andConditions.push(`{ status: { _eq: "${payload.status}" } }`);
        } else if (!payload.archived) {
          andConditions.push(`{ status: { _neq: "archived" } }`);
        }

        const where =
          andConditions.length > 0
            ? `where: { _and: [ ${andConditions.join(', ')} ] }`
            : '';

        // Sort order - support server-side sorting, default to updated_at desc
        const order = params.orderBy
          ? `order_by: ${params.orderBy}`
          : 'order_by: { updated_at: desc }';

        const clauses = [];
        if (where) clauses.push(where);
        clauses.push(order);

        // Pagination - 100 per page
        const [limitClause, offsetClause] = buildPaginationClauses({
          params,
          hasSearch: false,
        });
        clauses.push(limitClause);
        clauses.push(offsetClause);

        const finalClauses = clauses.join(', ');

        return {
          body: gql`
            query {
              product(${finalClauses}) ${productType}
            }
          `,
        };
      },
      cacheKey: (params) => params.cacheKey,
    }),
    searchProductsGlobal: builder.query({
      query: ({ p_query, p_region }) => ({
        body: gql`
          query {
            search_products(args: { p_query: "${p_query}"${p_region ? `, p_region: "${p_region}"` : ''} }) ${productType}
          }
        `,
      }),
      transformResponse: (response) => response['search_products'],
      providesTags: ['Product'],
    }),
    updateProduct: builder.mutation({
      query: (product) => {
        product = escapeBackslashes(product);
        return {
          body: gql`
          mutation {
            delete_certification(where: {product_id: {_eq: ${product.id}}}) {affected_rows}
            insert_certification(
              objects: ${objectToStr(
                product.certifications.map((c) => ({ ...c, product_id: product.id }))
              )}
            ) {affected_rows}
            update_product_by_pk(pk_columns: {id: ${product.id}}, _set: {
              item: "${product.item}", name: "${product.name}", 
              description: ${objectToStr(product.description).replace(/\t/g, ' ')},
              ${
                product.descriptive
                  ? `descriptive: ${objectToStr(product.descriptive).replace(/\t/g, ' ')},`
                  : ''
              } 
              ${
                product.has_descriptive !== null && product.has_descriptive !== undefined
                  ? `has_descriptive: ${product.has_descriptive},`
                  : ''
              }
              ${
                product.outline
                  ? `outline: ${objectToStr(product.outline).replace(/\t/g, ' ')},`
                  : ''
              }
              ${
                product.has_outline !== null && product.has_outline !== undefined
                  ? `has_outline: ${product.has_outline},`
                  : ''
              }
              image_url: "${product.image}", datasheet: "${product.datasheet}",
              ${product.manufacturer_id ? `manufacturer_id: ${product.manufacturer.id},` : ''}
              ${product.architect ? `for_company_id: ${product.architect.id},` : ''} 
              code: "${product.code}", section: "${product.section}", 
              cis: "${product.cis}", caws: "${product.caws}",
              uniclass: "${product.uniclass}", uniclass_system: "${product.uniclass_system}",
              omniclass: "${product.omniclass}", 
              regions: ${objectToStr(product.regions)}, 
              guidance: "${product.guidance.replace(/"/g, '\\"').replace(/\n/g, '\\n')}",
              ${
                product.manufacturer_guidance
                  ? `manufacturer_guidance: "${product.manufacturer_guidance.replace(/"/g, '\\"').replace(
                      /\n/g,
                      '\\n'
                    )}"`
                  : ''
              }
              ${product.division ? `division: "${product.division}",` : `division: null,`}
            }) ${productType}
          }
          `,
        };
      },
      transformResponse: (response) => response.update_product_by_pk,
      onQueryStarted: afterUpdate('getProductsFilter'),
    }),
    getProductById: builder.query({
      query: (id) => ({
        body: gql`
        query {
          product_by_pk(id: ${id}) ${productType}
        }
        `,
      }),
      transformResponse: (response) => response.product_by_pk,
    }),
    updateProductById: update(builder, 'product', productType, afterUpdate('getProductsFilter')),
    insertProduct: insert(builder, 'product', productType, afterInsert('getProductsFilter')),
    getProductComments: builder.query({
      query: (params) => ({
        body: gql`
        query {
          product_comment(
            where: {product_id: {_eq: ${params.productId}}}, 
            order_by: {created_at: asc}
          ) ${messageType}
        }
        `,
      }),
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded }) {
        // create a websocket connection when the cache subscription starts
        const { currentUser } = firebase.auth();
        // const token = await currentUser.getIdToken();
        const client = await ws.getClient(currentUser);
        try {
          // wait for the initial query to resolve before proceeding
          const { data: loadedData } = await cacheDataLoaded;
          const lastMessageDate =
            loadedData[loadedData.length - 1]?.created_at || '2023-01-01T00:36:01.858631+00:00';

          const onNext = (pushed) => {
            /* handle incoming values */
            const row = pushed?.data?.product_comment_stream[0];
            if (row?.profile?.id !== currentUser.uid) {
              updateCachedData((draft) => {
                draft.push(row);
              });
            }
          };

          let unsubscribe = () => {
            /* complete the subscription */
          };

          await new Promise((resolve, reject) => {
            unsubscribe = client.subscribe(
              {
                query: gql`
                subscription {
                  product_comment_stream(
                    batch_size: 1, 
                    cursor: {initial_value: {created_at: "${lastMessageDate}"}, ordering: ASC}, 
                    where: {product_id: {_eq: ${arg.productId}}})
                  ${messageType}
                }
                `,
              },
              {
                next: onNext,
                error: reject,
                complete: resolve,
              }
            );
          });
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        }
      },
      transformResponse: (response) => response.product_comment,
      providesTags: ['ProductComment'],
    }),
    insertProductComment: insert(
      builder,
      'product_comment',
      messageType,
      afterInsert('getProductComments')
    ),
    updateProductComment: update(
      builder,
      'product_comment',
      messageType,
      beforeUpdate('getProductComments', true)
    ),
    getProductCompanies: builder.query({
      query: (params) => ({
        body: gql`
          query {
            product_company(
              where: {
                ${params.company_id ? `company_id: {_eq: ${params.company_id}}` : ''}
                ${params.is_star ? `is_star: {_eq: ${params.is_star}}` : ''}
              }
            ) ${productCompanyType}
          }
        `,
      }),
      transformResponse: (response) => response.product_company,
      providesTags: (result, error, params) => {
        if (result) {
          // Provide tags for cache invalidation
          return result.map(({ id }) => ({ type: 'ProductCompany', id }));
        }
        // If no result, provide a generic tag
        return [{ type: 'ProductCompany', id: 'LIST' }];
      },
      onCacheEntryAdded: async (arg, { cacheDataLoaded, cacheEntryRemoved, refetch }) => {
        try {
          // Wait for the initial query to resolve
          await cacheDataLoaded;

          // Refetch the data when necessary
          const unsubscribe = cacheEntryRemoved.then(() => {
            refetch();
          });

          // Cleanup when the cache entry is removed
          await unsubscribe;
        } catch (error) {
          console.error('Error in onCacheEntryAdded for getProductCompanies:', error);
        }
      },
    }),
    insertProductCompany: insert(
      builder,
      'product_company',
      productCompanyType,
      afterInsert('getProductCompanies')
    ),
    updateProductCompany: update(
      builder,
      'product_company',
      productCompanyType,
      afterUpdate('getProductCompanies')
    ),
    getProductRecommendations: builder.query({
      query: (params) => ({
        body: gql`
          query {
            product_recommendation(
              where: {
                ${params.product_id ? `product_id: {_eq: ${params.product_id}}` : ''}
                ${
                  params.product_ids
                    ? `product_id: {_in: [${params.product_ids.join(',')}]}`
                    : ''
                }
                ${params.company_id ? `company_id: {_eq: ${params.company_id}}` : ''}
                ${
                  params.company_ids
                    ? `company_id: {_in: [${params.company_ids.join(',')}]}`
                    : ''
                }
              }
            ) ${productRecommendationType}
          }
        `,
      }),
      transformResponse: (response) => response.product_recommendation,
      providesTags: (result) => {
        if (result) {
          return result.map(({ id }) => ({ type: 'ProductRecommendation', id }));
        }
        return [{ type: 'ProductRecommendation', id: 'LIST' }];
      },
    }),
    insertProductRecommendation: insert(
      builder,
      'product_recommendation',
      productRecommendationType,
      afterInsert('getProductRecommendations')
    ),
    updateProductRecommendation: update(
      builder,
      'product_recommendation',
      productRecommendationType,
      afterUpdate('getProductRecommendations')
    ),
  }),
  overrideExisting: false,
});

export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useSearchProductsQuery,
  useSearchProductsGlobalQuery,
  useGetProductCommentsQuery,
  useGetProductsFilterQuery,
  useInsertProductCommentMutation,
  useUpdateProductCommentMutation,
  useUpdateProductMutation,
  useUpdateProductByIdMutation,
  useInsertProductMutation,
  useGetProductCompaniesQuery,
  useUpdateProductCompanyMutation,
  useInsertProductCompanyMutation,
  useGetProductRecommendationsQuery,
  useUpdateProductRecommendationMutation,
  useInsertProductRecommendationMutation,
} = productApi;
